// SPDX-License-Identifier: AGPL-3.0-or-later

/**
 * What to do with a cached channel when the gateway reconnects without resuming.
 *
 * A non-resuming reconnect means the socket missed message events, so cached channels can no longer be
 * trusted verbatim. Clearing all of them is safe but blunt: every channel opened afterwards blanks
 * behind a loading skeleton until its refetch lands. Retaining the contents and marking them stale
 * keeps the switch instant and moves the refetch into the background.
 */
export type ChannelMessagesReconnectAction =
	/** The channel the user is looking at: refetch it now, it is on screen. */
	| 'hydrate'
	/** Keep the cached messages renderable and refetch when the channel is next selected. */
	| 'retainStale'
	/** Nothing worth keeping (or the channel is gone): drop the entry. */
	| 'clear';

export interface ChannelMessagesReconnectInput {
	isSelectedChannel: boolean;
	channelExists: boolean;
	ready: boolean;
	messageCount: number;
}

export function selectChannelMessagesReconnectAction(
	input: ChannelMessagesReconnectInput,
): ChannelMessagesReconnectAction {
	if (!input.channelExists) return 'clear';
	if (input.isSelectedChannel) return 'hydrate';
	if (input.ready && input.messageCount > 0) return 'retainStale';
	return 'clear';
}

export interface ChannelMessagesRevalidateInput {
	isGatewayConnected: boolean;
	loadingMore: boolean;
	stale: boolean;
}

/**
 * Whether selecting a channel should kick a background refetch. Only stale entries qualify, and only
 * when nothing is already in flight, so a normal channel switch still costs no request.
 */
export function shouldRevalidateStaleChannelOnSelect(input: ChannelMessagesRevalidateInput): boolean {
	if (!input.isGatewayConnected) return false;
	if (input.loadingMore) return false;
	return input.stale;
}
